<script lang="ts">
  import { onDestroy, onMount, untrack, type Snippet } from "svelte"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import Input from "@kontextwerk/web-sdk/ui/forms/Input.svelte"
  import ActionBox from "@kontextwerk/web-sdk/ui/layout/ActionBox.svelte"
  import Icon from "@kontextwerk/web-sdk/ui/primitives/Icon.svelte"
  import SectionTitle from "@kontextwerk/web-sdk/ui/typography/SectionTitle.svelte"
  import { newApproval } from "@kontextwerk/web-sdk/core/overlay/approvals"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import { registerNavigationGuard } from "@kontextwerk/web-sdk/core/routing/navigation"
  import { mdiContentSave, mdiRestore } from "@mdi/js"
  import type { OrganizationRoleResponse } from "@kontextwerk/control-web-client"
  import { refreshControlContext } from "@kontextwerk/control-web-frame/shared/logic/authentication"
  import type { PermissionState } from "@kontextwerk/control-web-frame/shared/logic/authorization"
  import type { IdentityAdministrationApi } from "../../../../../../../../../../../../api/kehrblatt/identity-administration"
  import PermissionsGrid from "../../../../../../../../../../_shared/_modules/permissions-grid"
  import {
    PERMISSION_SECTIONS,
    grantsFromPermissionState,
    permissionStateFromGrants,
    permissionStatesEqual,
    type PermissionGridState,
    type PermissionLevel,
    type PermissionRowConfig,
  } from "../../../../../../../../../../_shared/_logic/permission-sections"
  import { roleKindLabel } from "../../../../../../_shared/_logic/role-presentation"
  import { replaceRole } from "./_actions/replaceRole"
  import { delegableLevelOptions, delegableScopeOptions } from "./_logic/delegableOptions"

  type Props = {
    roleId: string
    access: IdentityAdministrationApi
    /** core.roles manage: own roles may be edited. */
    canManage: boolean
    permissions: PermissionState
    /** Role of the confirmed actor; changing it re-reads the actor's own authority. */
    actorRoleId: string | null
    isCurrent: () => boolean
    onSaved: (role: OrganizationRoleResponse) => void | Promise<unknown>
    onremoveOverlay?: () => void
    onshowOverlay?: () => void
    setActionButtonSnippet?: (snippet: Snippet | null) => void
    setCloseDisabled?: (disabled: boolean) => void
    setBeforeClose?: (guard: (() => boolean | Promise<boolean>) | null) => void
  }

  let {
    roleId,
    access,
    canManage,
    permissions,
    actorRoleId,
    isCurrent,
    onSaved,
    onremoveOverlay = () => {},
    onshowOverlay = () => {},
    setActionButtonSnippet = () => {},
    setCloseDisabled = () => {},
    setBeforeClose = () => {},
  }: Props = $props()

  type Load = { status: "loading" } | { status: "ready" } | { status: "failed" }

  const local = new AbortController()
  let load = $state<Load>({ status: "loading" })
  let baseline = $state.raw<OrganizationRoleResponse | null>(null)
  let baselineGrid = $state.raw<PermissionGridState | null>(null)
  let name = $state("")
  let draft = $state<PermissionGridState>({ levels: {}, scopes: {} })
  let saving = $state(false)
  let confirmation: Promise<boolean> | null = null

  const current = () => !local.signal.aborted && isCurrent()
  const editable = $derived(Boolean(canManage && baseline?.kind === "custom"))
  const dirty = $derived(
    Boolean(
      editable &&
      baseline &&
      baselineGrid &&
      (name.trim() !== baseline.name || !permissionStatesEqual(draft, baselineGrid)),
    ),
  )
  const nameValid = $derived(name.trim().length > 0 && name.trim().length <= 100)
  const readOnlyHint = $derived(
    !baseline
      ? null
      : baseline.kind !== "custom"
        ? `${roleKindLabel(baseline.kind)} ist eine Systemrolle. Ihre Berechtigungen sind fest vorgegeben.`
        : canManage
          ? null
          : "Sie können diese Rolle nur ansehen.",
  )

  const applyBaseline = (role: OrganizationRoleResponse) => {
    const grid = permissionStateFromGrants(role.grants)
    baseline = role
    baselineGrid = grid
    name = role.name
    draft = { levels: { ...grid.levels }, scopes: { ...grid.scopes } }
  }

  const loadRole = async () => {
    load = { status: "loading" }
    try {
      const role = await access.role(
        untrack(() => roleId),
        local.signal,
      )

      if (!current()) return

      applyBaseline(role)
      load = { status: "ready" }
    } catch {
      if (current()) load = { status: "failed" }
    }
  }

  const discard = () => {
    if (!baseline || saving) return

    applyBaseline(baseline)
  }

  const updateLevel = (key: string, level: PermissionLevel) => {
    if (!editable || saving) return

    draft = { ...draft, levels: { ...draft.levels, [key]: level } }
  }

  const updateScope = (key: string, scope: string | null) => {
    if (!editable || saving) return

    draft = {
      ...draft,
      scopes: { ...draft.scopes, [key]: scope === "own" || scope === "all" ? scope : null },
    }
  }

  const save = async () => {
    const role = baseline

    if (!role || !editable || !dirty || !nameValid || saving || !current()) return

    saving = true
    setCloseDisabled(true)
    let result: Awaited<ReturnType<typeof replaceRole>>
    try {
      result = await replaceRole({
        api: access,
        role,
        name,
        grants: grantsFromPermissionState(draft),
      })
    } finally {
      saving = false

      if (!local.signal.aborted) setCloseDisabled(false)
    }

    if (!current()) return

    if (result.status === "done") {
      applyBaseline(result.value.role)
      newNotification({ class: "success", html: "Rolle wurde gespeichert." })
      void onSaved(result.value.role)

      // The actor's own grants changed: re-read the confirmed context before anything else.
      if (result.value.changed && actorRoleId?.toLowerCase() === role.id.toLowerCase())
        void refreshControlContext()
    } else if (result.status === "conflict") {
      newNotification({
        class: "info",
        html: "Die Rolle wurde zwischenzeitlich geändert. Die aktuelle Fassung wurde geladen.",
      })
      await loadRole()
    } else if (result.status === "forbidden") {
      newNotification({
        class: "error",
        html: "Für diese Änderung fehlt die Berechtigung. Sie können nur Rechte vergeben, die Sie selbst besitzen.",
      })
    } else if (result.status === "failed") {
      newNotification({ class: "error", html: "Rolle konnte nicht gespeichert werden." })
    }
  }

  const beforeClose = async () => {
    if (!current() || !dirty) return true

    if (saving) return false

    confirmation ??= newApproval({
      title: "Ungespeicherte Änderungen",
      description: "Änderungen an der Rolle verwerfen?",
      approvalLabel: "Verwerfen",
      declineLabel: "Weiter bearbeiten",
    }).finally(() => {
      confirmation = null
    })

    const approved = await confirmation

    return !current() || approved
  }

  const levelOptionsForRow = (row: PermissionRowConfig) =>
    delegableLevelOptions(row, permissions, baselineGrid?.levels[row.key])

  const scopeOptionsForRow = (row: PermissionRowConfig) =>
    delegableScopeOptions(
      row,
      permissions,
      draft.levels[row.key],
      baselineGrid?.scopes[row.key] ?? null,
    )

  onMount(() => {
    onshowOverlay()
    setActionButtonSnippet(null)
    setBeforeClose(beforeClose)
    void loadRole()
    const unregister = registerNavigationGuard(
      `organization-role-${untrack(() => roleId)}`,
      (_to, _options, resume) => {
        if (!current() || (!dirty && !saving)) return true

        void beforeClose().then((allowed) => {
          if (allowed && current()) {
            discard()
            onremoveOverlay()
            resume?.()
          }
        })

        return false
      },
    )

    return () => unregister()
  })

  onDestroy(() => {
    local.abort()
    setBeforeClose(null)
    setActionButtonSnippet(null)
  })
</script>

<div class="role-overlay form light" aria-busy={load.status === "loading" || saving}>
  {#if load.status === "loading"}
    <p class="hint" role="status">Rolle wird geladen …</p>
  {:else if load.status === "failed" || !baseline}
    <div class="state-row" role="alert">
      <p class="hint">Rolle konnte nicht geladen werden.</p>
      <CTA type="secondaryButton" content="Erneut versuchen" onClick={() => void loadRole()} />
    </div>
  {:else}
    <SectionTitle title={editable ? "Rolle bearbeiten" : "Berechtigungen der Rolle"}>
      {#snippet buttonSnippet()}
        {#if editable}
          <CTA
            type="primaryButton"
            content={saving ? "Speichere…" : "Änderungen speichern"}
            onClick={() => void save()}
            disabled={saving || !dirty || !nameValid}
            options={{
              label: "Rollenoptionen",
              disabled: saving,
              items: [
                {
                  id: "discard",
                  label: "Änderungen verwerfen",
                  icon: mdiRestore,
                  disabled: saving || !dirty,
                  onClick: discard,
                },
              ],
            }}
          >
            {#snippet iconSnippet()}
              <Icon path={mdiContentSave} />
            {/snippet}
          </CTA>
        {/if}
      {/snippet}
    </SectionTitle>

    <ActionBox label="Rolle" darkMode={false}>
      {#snippet children()}
        <div class="role-facts">
          {#if editable}
            <Input
              type="text"
              placeholder="Rollenname"
              value={name}
              maxlength={100}
              required={true}
              disabled={saving}
              brightBackground={true}
              onChange={(event) => {
                name = (event.target as HTMLInputElement | null)?.value ?? ""
              }}
            />
            {#if !nameValid}
              <p class="hint" role="alert">Bitte einen Rollennamen angeben.</p>
            {/if}
          {:else}
            <p class="role-name">{baseline.name}</p>
          {/if}
          <p class="hint">Art: {roleKindLabel(baseline.kind)}</p>
          {#if readOnlyHint}
            <p class="hint">{readOnlyHint}</p>
          {/if}
        </div>
      {/snippet}
    </ActionBox>

    <PermissionsGrid
      sections={PERMISSION_SECTIONS}
      levels={editable ? draft.levels : (baselineGrid?.levels ?? {})}
      scopes={editable ? draft.scopes : (baselineGrid?.scopes ?? {})}
      readonly={!editable}
      invertColors={true}
      brightInputs={true}
      {levelOptionsForRow}
      {scopeOptionsForRow}
      onLevelChange={updateLevel}
      onScopeChange={updateScope}
      isLevelDisabled={() => saving}
      isScopeDisabled={(row) => saving || (draft.levels[row.key] ?? "none") === "none"}
    />
  {/if}
</div>

<style lang="less">
  .role-overlay {
    display: flex;
    flex-direction: column;
    gap: 1.2rem;
    color: var(--text-100);
    min-width: 0;
  }

  .role-facts {
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
    padding-top: 0.4rem;
  }

  .role-name {
    margin: 0;
    font-weight: 600;
    overflow-wrap: anywhere;
  }

  .hint {
    margin: 0;
    line-height: 1.5;
    color: var(--text-100);
    overflow-wrap: anywhere;
  }

  .state-row {
    display: flex;
    align-items: center;
    gap: 0.8rem;
    flex-wrap: wrap;
  }
</style>
